import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const formatErr = (detail) => {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const AdminLoginPage = () => {
  const { login, user, isChecking } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!isChecking && user) {
    const dest = location.state?.from || "/admin";
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-light via-white to-white p-4" data-testid="admin-login-page">
      <div className="bg-white p-8 rounded-2xl shadow-card w-full max-w-md">
        <h1 className="font-display text-2xl font-bold text-gray-dark mb-2">Admin sign in</h1>
        <p className="text-gray-medium text-sm mb-6">FunAsia Digital — content & lead management</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-dark mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none"
              data-testid="admin-login-email"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-dark mb-1">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none"
              data-testid="admin-login-password"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="admin-login-error">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            <LogIn size={16} />
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;
